import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMemo, useState } from "react";
import { Transaction } from "@/types";
import { useTranslation } from "react-i18next";

type Props = {
  transactions?: Transaction[];
};

export default function TransactionsTab({ transactions }: Props) {
  const { t } = useTranslation();
  // Filters
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  const filtered = useMemo(() => {
    if (!transactions) return [];
    return transactions.filter((t: Transaction) => {
      // search by stock name or notes
      const name = (t.Stock?.name || "").toString().toLowerCase();
      const notes = (t.notes || "").toString().toLowerCase();
      if (search) {
        const q = search.toLowerCase();
        if (!name.startsWith(q) && !notes.startsWith(q)) return false;
      }

      if (typeFilter && typeFilter !== "all") {
        if ((t.transaction_type || "") !== typeFilter) return false;
      }

      if (statusFilter && statusFilter !== "all") {
        if ((t.status || "").toString() !== statusFilter) return false;
      }

      if (fromDate) {
        const d = new Date(t.transaction_date || t.created_at);
        const f = new Date(fromDate);
        if (isNaN(d.getTime()) || d < f) return false;
      }
      if (toDate) {
        const d = new Date(t.transaction_date || t.created_at);
        const tDate = new Date(toDate);
        // include whole day
        tDate.setHours(23, 59, 59, 999);
        if (isNaN(d.getTime()) || d > tDate) return false;
      }

      return true;
    });
  }, [transactions, search, typeFilter, statusFilter, fromDate, toDate]);
  return (
    <div className="mt-6">
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Input
              placeholder={t("TransactionsTab.Filters.SearchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full"
            />

            <Select onValueChange={(v) => setTypeFilter(v)}>
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={t("TransactionsTab.Filters.AllTypes")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("TransactionsTab.Filters.AllTypes")}
                </SelectItem>
                <SelectItem value="stock_in">
                  {t("TransactionsTab.Filters.StockIn")}
                </SelectItem>
                <SelectItem value="stock_out">
                  {t("TransactionsTab.Filters.StockOut")}
                </SelectItem>
                <SelectItem value="manual_adjustment">
                  {t("TransactionsTab.Filters.ManualAdjustment")}
                </SelectItem>
              </SelectContent>
            </Select>

            <Select onValueChange={(v) => setStatusFilter(v)}>
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={t("TransactionsTab.Filters.AllStatuses")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("TransactionsTab.Filters.AllStatuses")}
                </SelectItem>
                <SelectItem value="completed">
                  {t("TransactionsTab.Filters.Completed")}
                </SelectItem>
                <SelectItem value="pending">
                  {t("TransactionsTab.Filters.Pending")}
                </SelectItem>
              </SelectContent>
            </Select>

            <div className="grid grid-cols-2 gap-2">
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                placeholder={t("TransactionsTab.Filters.FromDate")}
                className="text-sm"
              />
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                placeholder={t("TransactionsTab.Filters.ToDate")}
                className="text-sm"
              />
            </div>
          </div>

          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t("TransactionsTab.TableHeaders.Item")}
                  </TableHead>
                  <TableHead>
                    {t("TransactionsTab.TableHeaders.Type")}
                  </TableHead>
                  <TableHead>
                    {t("TransactionsTab.TableHeaders.QuantityChange")}
                  </TableHead>
                  <TableHead>
                    {t("TransactionsTab.TableHeaders.Status")}
                  </TableHead>
                  <TableHead>
                    {t("TransactionsTab.TableHeaders.PerformedBy")}
                  </TableHead>
                  <TableHead>
                    {t("TransactionsTab.TableHeaders.Date")}
                  </TableHead>
                  <TableHead>
                    {t("TransactionsTab.TableHeaders.Notes")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex flex-col items-center justify-center space-y-2 text-muted-foreground">
                        <div className="text-lg font-medium">
                          {t("TransactionsTab.EmptyState.Title")}
                        </div>
                        <div className="text-sm">
                          {!transactions || transactions.length === 0
                            ? t("TransactionsTab.EmptyState.NoData")
                            : t("TransactionsTab.EmptyState.AdjustFilters")}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((txn: Transaction) => (
                    <TableRow key={txn.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {txn.Stock?.name ||
                              t("TransactionsTab.Labels.Unknown")}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {t("TransactionsTab.Labels.ID")}: {txn.stock_id}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            txn.transaction_type === "stock_in"
                              ? "default"
                              : txn.transaction_type === "stock_out"
                                ? "destructive"
                                : txn.transaction_type === "manual_adjustment"
                                  ? "secondary"
                                  : "outline"
                          }
                        >
                          {txn.transaction_type
                            ?.replace("_", " ")
                            .toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {txn.previous_quantity} → {txn.new_quantity}{" "}
                            {txn.Stock?.unit}
                          </div>
                          <div className="text-sm flex items-center gap-1">
                            <span
                              className={`font-semibold ${
                                txn.transaction_type === "stock_in"
                                  ? "text-green-600"
                                  : txn.transaction_type === "stock_out"
                                    ? "text-red-600"
                                    : txn.transaction_type ===
                                        "manual_adjustment"
                                      ? "text-blue-600"
                                      : "text-muted-foreground"
                              }`}
                            >
                              {txn.transaction_type === "stock_in" && "📈 +"}
                              {txn.transaction_type === "stock_out" && "📉 -"}
                              {txn.transaction_type === "manual_adjustment" &&
                                "⚙️ "}
                              {!txn.transaction_type &&
                                (Number(txn.quantity) > 0 ? "+" : "-")}
                              {Math.abs(Number(txn.quantity))} {txn.Stock?.unit}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            txn.status === "completed"
                              ? "default"
                              : txn.status === "pending"
                                ? "secondary"
                                : "outline"
                          }
                        >
                          {txn.status?.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{txn.User.name}</div>
                          {txn.approved_by && (
                            <div className="text-sm text-muted-foreground">
                              {t("TransactionsTab.Labels.ApprovedBy")}:{" "}
                              {txn.approved_by}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {new Date(
                              txn.transaction_date || txn.created_at
                            ).toLocaleDateString()}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {new Date(
                              txn.transaction_date || txn.created_at
                            ).toLocaleTimeString()}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs">
                          <div className="text-sm">{txn.notes || "—"}</div>
                          {txn.reference_type && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {t("TransactionsTab.Labels.Ref")}:{" "}
                              {txn.reference_type}
                            </div>
                          )}
                          {txn.total_cost && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {t("TransactionsTab.Labels.Cost")}: $
                              {txn.total_cost}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
